import React from 'react';
import { useTranslation } from 'react-i18next';
import { type TwilioMessage, Message } from '@twilio/conversations';
import { LiveChatItem, LiveChatQueryResponse } from 'app/hooks/fetch/useFetchLiveChat';

import TwilioMessageFactory from './TwilioMessageFactory';
import MessageFactory from './MessageFactory';

type Props = {
  isConnectingToNurse: boolean;
  isTyping: boolean;
  mostRecentMessage: LiveChatItem | TwilioMessage;
  identity: string;
  liveChat: LiveChatQueryResponse;
  handleHTMLLinkClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleSubmit: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
  handleSkip: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
};

export const AccessibilityLiveChatRegion = ({
  isConnectingToNurse,
  isTyping,
  mostRecentMessage,
  identity,
  liveChat,
  handleHTMLLinkClick,
  handleSubmit,
  handleSkip,
}: Props) => {
  const { t } = useTranslation();

  const generateLastSrMessage = () => {
    if (isConnectingToNurse) {
      return <p>{t('connecting_message')}</p>;
    }
    if (isTyping) {
      return <p>{t('typing')}</p>;
    }
    if (mostRecentMessage instanceof Message) {
      return (
        <TwilioMessageFactory
          isAuthor={identity === mostRecentMessage?.author}
          message={mostRecentMessage as TwilioMessage}
        />
      );
    }
    return (
      <MessageFactory
        item={mostRecentMessage as LiveChatItem}
        index={liveChat.chat_items.indexOf(mostRecentMessage as LiveChatItem)}
        array={liveChat.chat_items}
        liveChat={liveChat}
        onClickConsentLink={handleHTMLLinkClick}
        onSubmitMessageForm={handleSubmit}
        onSkipMessageForm={handleSkip}
        isLoadingMessageForm={false}
      />
    );
  };

  return (
    <div
      className="sr-only"
      aria-live="polite"
      role="log"
      aria-label="Live Chat Conversation"
      aria-relevant="additions text"
    >
      <p className="sr-only">
        Use Control End to jump to the latest message, Control Home to jump to the first message.
      </p>
      {generateLastSrMessage()}
    </div>
  );
};
